import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, isToday } from 'date-fns';
import type {
  UserAnalyticsResponse,
  MatterAnalyticsResponse,
  UserSortField,
  MatterSortField,
  SortOrder,
} from '@/hooks/useAnalytics';
import { Loading, MatterMemberAvatars } from '@/components/shared';
import { SortableHeader } from './SortableHeader';
import { formatNumber, PAGE_SIZE } from './chart-utils';

interface TopUsersMattersProps {
  usersData: UserAnalyticsResponse | undefined;
  usersLoading: boolean;
  mattersData: MatterAnalyticsResponse | undefined;
  mattersLoading: boolean;
}

export const TopUsersMatters = ({ usersData, usersLoading, mattersData, mattersLoading }: TopUsersMattersProps) => {
  const [userSort, setUserSort] = useState<{ field: UserSortField; order: SortOrder }>({
    field: 'total_tokens',
    order: 'desc',
  });
  const [matterSort, setMatterSort] = useState<{ field: MatterSortField; order: SortOrder }>({
    field: 'documents_count',
    order: 'desc',
  });
  const [usersPage, setUsersPage] = useState(1);
  const [mattersPage, setMattersPage] = useState(1);

  // Client-side sorting for users
  const sortedUsers = useMemo(() => {
    if (!usersData?.users) return [];
    const sorted = [...usersData.users];
    sorted.sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;
      switch (userSort.field) {
        case 'name':
          aVal = a.user_name.toLowerCase();
          bVal = b.user_name.toLowerCase();
          break;
        case 'total_tokens':
          aVal = a.total_tokens;
          bVal = b.total_tokens;
          break;
        case 'documents_uploaded':
          aVal = a.documents_uploaded;
          bVal = b.documents_uploaded;
          break;
        case 'messages_sent':
          aVal = a.messages_sent;
          bVal = b.messages_sent;
          break;
        case 'last_activity':
          aVal = a.last_activity_date || '';
          bVal = b.last_activity_date || '';
          break;
        case 'active_days':
          aVal = a.active_days || 0;
          bVal = b.active_days || 0;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return userSort.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return userSort.order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [usersData?.users, userSort.field, userSort.order]);

  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * PAGE_SIZE;
    return sortedUsers.slice(start, start + PAGE_SIZE);
  }, [sortedUsers, usersPage]);

  // Client-side sorting for matters
  const sortedMatters = useMemo(() => {
    if (!mattersData?.matters) return [];
    const sorted = [...mattersData.matters];
    sorted.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (matterSort.field) {
        case 'name':
          aVal = a.matter_name.toLowerCase();
          bVal = b.matter_name.toLowerCase();
          break;
        case 'documents_count':
          aVal = a.documents_count;
          bVal = b.documents_count;
          break;
        case 'messages_count':
          aVal = a.messages_count;
          bVal = b.messages_count;
          break;
        case 'users_count':
          aVal = a.users_count;
          bVal = b.users_count;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return matterSort.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return matterSort.order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [mattersData?.matters, matterSort.field, matterSort.order]);

  const paginatedMatters = useMemo(() => {
    const start = (mattersPage - 1) * PAGE_SIZE;
    return sortedMatters.slice(start, start + PAGE_SIZE);
  }, [sortedMatters, mattersPage]);

  const handleUserSort = (field: string) => {
    setUserSort(prev => ({
      field: field as UserSortField,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
    setUsersPage(1);
  };

  const handleMatterSort = (field: string) => {
    setMatterSort(prev => ({
      field: field as MatterSortField,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
    setMattersPage(1);
  };

  const totalUsersPages = Math.ceil(sortedUsers.length / PAGE_SIZE);
  const totalMattersPages = Math.ceil(sortedMatters.length / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Top Users & Matters</CardTitle>
        <CardDescription>
          User and matter activity
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="users">Top Users</TabsTrigger>
            <TabsTrigger value="matters">Top Matters</TabsTrigger>
          </TabsList>

          {/* Top Users Tab */}
          <TabsContent value="users">
          {usersLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loading />
            </div>
          ) : (
            <>
              <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      label="User"
                      field="name"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      width="40%"
                    />
                    <SortableHeader
                      label="Tokens"
                      field="total_tokens"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      className="text-right"
                      width="100px"
                    />
                    <SortableHeader
                      label="Documents"
                      field="documents_uploaded"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      className="text-right"
                      width="80px"
                    />
                    <SortableHeader
                      label="Messages"
                      field="messages_sent"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      className="text-right"
                      width="80px"
                    />
                    <SortableHeader
                      label="Active Days"
                      field="active_days"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      className="text-right"
                      width="100px"
                    />
                    <SortableHeader
                      label="Last Active"
                      field="last_activity"
                      currentField={userSort.field}
                      currentOrder={userSort.order}
                      onSort={handleUserSort}
                      className="text-right"
                      width="100px"
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow key={user.user_guid}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{user.user_name}</p>
                              {!user.is_active && (
                                <Badge
                                  variant="outline"
                                  className="border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
                                >
                                  Account inactive
                                </Badge>
                              )}
                            </div>
                            {user.user_email && (
                              <p className="text-xs text-muted-foreground">{user.user_email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatNumber(user.total_tokens)}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.documents_uploaded}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.messages_sent}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.active_days || 0}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {user.last_activity_date
                            ? (isToday(new Date(user.last_activity_date)) ? 'Today' : format(new Date(user.last_activity_date), 'MMM d'))
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                        No user data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
              {totalUsersPages > 1 && (
                <div className="mt-2 flex items-center justify-between px-2">
                  <span className="text-sm text-muted-foreground">
                    Page {usersPage} of {totalUsersPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={ChevronLeft}
                      aria-label="Previous page"
                      onClick={() => setUsersPage(Math.max(1, usersPage - 1))}
                      disabled={usersPage === 1}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      icon={ChevronRight}
                      aria-label="Next page"
                      onClick={() => setUsersPage(Math.min(totalUsersPages, usersPage + 1))}
                      disabled={usersPage >= totalUsersPages}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          </TabsContent>

          {/* Top Matters Tab */}
          <TabsContent value="matters">
          {mattersLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loading />
            </div>
          ) : (
            <>
              <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      label="Matter"
                      field="name"
                      currentField={matterSort.field}
                      currentOrder={matterSort.order}
                      onSort={handleMatterSort}
                      width="40%"
                    />
                    <SortableHeader
                      label="Documents"
                      field="documents_count"
                      currentField={matterSort.field}
                      currentOrder={matterSort.order}
                      onSort={handleMatterSort}
                      className="text-right"
                      width="100px"
                    />
                    <SortableHeader
                      label="Messages"
                      field="messages_count"
                      currentField={matterSort.field}
                      currentOrder={matterSort.order}
                      onSort={handleMatterSort}
                      className="text-right"
                      width="100px"
                    />
                    <TableHead className="text-right" style={{ width: '140px' }}>Members</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedMatters.length > 0 ? (
                    paginatedMatters.map((matter) => (
                      <TableRow key={matter.matter_guid}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{matter.matter_name}</p>
                            {matter.case_reference && (
                              <p className="text-xs text-muted-foreground">{matter.case_reference}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {matter.documents_count}
                        </TableCell>
                        <TableCell className="text-right">
                          {matter.messages_count}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <MatterMemberAvatars members={matter.members || []} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                        No matter data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
              {totalMattersPages > 1 && (
                <div className="mt-2 flex items-center justify-between px-2">
                  <span className="text-sm text-muted-foreground">
                    Page {mattersPage} of {totalMattersPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={ChevronLeft}
                      aria-label="Previous page"
                      onClick={() => setMattersPage(Math.max(1, mattersPage - 1))}
                      disabled={mattersPage === 1}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      icon={ChevronRight}
                      aria-label="Next page"
                      onClick={() => setMattersPage(Math.min(totalMattersPages, mattersPage + 1))}
                      disabled={mattersPage >= totalMattersPages}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
